import React, { useRef, useState, useCallback } from "react";
import { MoveHorizontal } from "lucide-react";
import { useScrollReveal } from "@/hooks/useScrollReveal";

// Import images
import afterImage from "@/assets/images/after.png";
import beforeImage from "@/assets/images/before.png";

export default function BeforeAfter() {
  const { ref, visible } = useScrollReveal();
  const [position, setPosition] = useState(50);
  const containerRef = useRef(null);
  const dragging = useRef(false);

  const updatePosition = useCallback((clientX) => {
    const rect = containerRef.current.getBoundingClientRect();
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPosition(Math.max(0, Math.min(100, pct)));
  }, []);

  const onPointerMove = (e) => {
    if (!dragging.current) return;
    updatePosition(e.clientX);
  };

  const onPointerDown = (e) => {
    dragging.current = true;
    updatePosition(e.clientX);
  };

  const onPointerUp = () => {
    dragging.current = false;
  };

  return (
    <section className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Heading */}
        <div
          ref={ref}
          className={`text-center max-w-3xl mx-auto mb-10 transition-all duration-700 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          <div className="inline-flex items-center gap-2 mb-4">
            <div className="w-8 h-0.5 bg-[#434951]" />
            <span className="section-label text-[#194d32] text-base">
              BEFORE & AFTER
            </span>
            <div className="w-8 h-0.5 bg-[#434951]" />
          </div>
          <h2 className="headline text-[hsl(var(--navy))] text-4xl sm:text-5xl md:text-6xl mb-4">
            See the <br className="hidden sm:block" />
            Difference
          </h2>
          <p className="text-[hsl(var(--charcoal))]/70 text-base sm:text-lg max-w-2xl mx-auto">
            From worn or damaged concrete to clean, professionally finished
            surfaces, see how NCC transforms residential and commercial spaces.
          </p>
        </div>

        {/* Slider */}
        <div
          ref={containerRef}
          className="relative w-full h-[400px] sm:h-[500px] md:h-[600px] overflow-hidden rounded-sm cursor-ew-resize select-none border-2 border-[hsl(var(--concrete))]"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={onPointerUp}
        >
          {/* After (full) */}
          <div className="absolute inset-0">
            <img
              src={afterImage}
              alt="After NCC concrete work"
              className="w-full h-full object-cover"
            />
            <div className="absolute bottom-4 right-4 text-white font-heading text-sm tracking-wider px-3 py-1 rounded-sm bg-[#194d32]">
              AFTER
            </div>
          </div>

          {/* Before (clipped) */}
          <div
            className="absolute inset-0 overflow-hidden"
            style={{ width: `${position}%` }}
          >
            <div
              className="absolute inset-0"
              style={{ width: `${position > 0 ? 100 / (position / 100) : 100}%` }}
            >
              <img
                src={beforeImage}
                alt="Before NCC concrete work"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute bottom-4 left-4 bg-[hsl(var(--charcoal))] text-white font-heading text-sm tracking-wider px-3 py-1 rounded-sm">
              BEFORE
            </div>
          </div>

          {/* Handle */}
          <div
            className="absolute top-0 bottom-0 w-1 bg-[hsl(var(--charcoal))] -translate-x-1/2 pointer-events-none"
            style={{ left: `${position}%` }}
          >
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 bg-[hsl(var(--charcoal))] rounded-full flex items-center justify-center shadow-xl">
              <MoveHorizontal size={24} className="text-white" />
            </div>
          </div>
        </div>

        <p className="text-center text-[hsl(var(--charcoal))]/50 text-sm mt-4 flex items-center justify-center gap-2">
          <MoveHorizontal size={16} />
          Drag to compare
        </p>
      </div>
    </section>
  );
}